// teamLogoService.js
// Web port of the iOS TeamLogoService.
//
// Loads the team-logo URL map from Firestore (app_config/teamLogos) once per
// session and resolves a logo for a given (city, sport). Resolution order
// matches iOS:
//   1. Bundled asset (for teams the Highlightly API doesn't cover well)
//   2. Firestore URL
//   3. null  → caller falls back to the jersey number
//
// The Firestore doc is keyed by sport rawValue → { UPPERCASE_CITY: logoURL }.

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// Bundled logo files — drop these PNGs into src/assets/logos/.
import logo_baseball_FLORIDA      from "../assets/logos/logo_baseball_FLORIDA.png";
import logo_baseball_MONTREAL     from "../assets/logos/logo_baseball_MONTREAL.png";
import logo_basketball_NEW_JERSEY from "../assets/logos/logo_basketball_NEW_JERSEY.png";
import logo_basketball_SEATTLE    from "../assets/logos/logo_basketball_SEATTLE.png";
import logo_basketball_VANCOUVER  from "../assets/logos/logo_basketball_VANCOUVER.png";
import logo_football_ST_LOUIS     from "../assets/logos/logo_football_ST_LOUIS.png";
import logo_hockey_ARIZONA        from "../assets/logos/logo_hockey_ARIZONA.png";
import logo_hockey_ATLANTA        from "../assets/logos/logo_hockey_ATLANTA.png";
import logo_hockey_PHOENIX        from "../assets/logos/logo_hockey_PHOENIX.png";

// ─── Bundled logos ────────────────────────────────────────────────────────────
// Mirrors the iOS bundled assets. Drop each file into src/assets/logos/ using the
// iOS naming convention — logo_<sportId>_<SANITIZED_CITY>.png — then add an import
// and register it below. SANITIZED_CITY = uppercased city with spaces → "_" and
// any ( ) . characters removed (e.g. "ST. LOUIS" → "ST_LOUIS", "NEW YORK (I)" →
// "NEW_YORK_I"). The map key must equal the import's file name (without extension).
//
//   import logo_football_CHICAGO from "../assets/logos/logo_football_CHICAGO.png";
//
// const BUNDLED_LOGOS = {
//   logo_football_CHICAGO,
//   logo_hockey_ST_LOUIS,
//   ...
// };

const BUNDLED_LOGOS = {
  // Relocated / defunct franchises the Highlightly API doesn't cover.
  // Each key equals bundledKey(city, sportId) — logo_<sportId>_<SANITIZED_CITY>.
  logo_baseball_FLORIDA,
  logo_baseball_MONTREAL,
  logo_basketball_NEW_JERSEY,
  logo_basketball_SEATTLE,
  logo_basketball_VANCOUVER,
  logo_football_ST_LOUIS,
  logo_hockey_ARIZONA,
  logo_hockey_ATLANTA,
  logo_hockey_PHOENIX,
};

// Sport rawValue used as the top-level key in app_config/teamLogos.
const SPORT_RAW = {
  football:   "Football",
  baseball:   "Baseball",
  basketball: "Basketball",
  hockey:     "Hockey",
  soccer:     "Soccer",
};

// ─── Session cache for the Firestore URL map ──────────────────────────────────

let urlMap = null;       // { Football: { CITY: url }, ... } once loaded
let loadPromise = null;  // de-dupes concurrent load calls

/// Loads the logo URL map from Firestore once. Safe to call repeatedly — returns
/// the cached map after the first successful load. Never throws.
export async function loadTeamLogos() {
  if (urlMap) return urlMap;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const snap = await getDoc(doc(db, "app_config", "teamLogos"));
      urlMap = snap.exists() ? (snap.data() || {}) : {};
    } catch (err) {
      console.warn("teamLogoService: failed to load app_config/teamLogos", err);
      urlMap = {};
    }
    return urlMap;
  })();

  return loadPromise;
}

// Matches iOS bundledAssetName(): logo_<sportId>_<SANITIZED_CITY>
function bundledKey(city, sportId) {
  const sanitized = city
    .toUpperCase()
    .replace(/ /g, "_")
    .replace(/[().]/g, "");
  return `logo_${sportId}_${sanitized}`;
}

/// Bundled asset source for (city, sport), or null. Available synchronously.
export function bundledLogo(city, sportId) {
  if (!city) return null;
  return BUNDLED_LOGOS[bundledKey(city, sportId)] || null;
}

/// Firestore URL for (city, sport), or null. Requires loadTeamLogos() to have run;
/// returns null (jersey fallback) until then.
export function logoURL(city, sportId) {
  if (!urlMap || !city) return null;
  const sportMap = urlMap[SPORT_RAW[sportId]];
  if (!sportMap) return null;
  return sportMap[city.toUpperCase()] || null;
}
